import * as React from "react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { registerAppBridge } from "../app-bridge.ts";
import { router } from "../router.tsx";
import { PracticeRouteSync } from "../providers/practice-route-sync.tsx";
import { useNarrowLayout } from "../hooks/use-narrow-layout.ts";
import { Sidebar } from "./sidebar.component.tsx";
import { TopBar } from "./top-bar.component.tsx";
import { ChatDockProvider } from "../../shared/chat-dock/chat-dock.component.tsx";
import { DockingEditBarHostProvider } from "../../shared/docking/docking-edit-bar-host.tsx";

const SIDEBAR_STORAGE_KEY = "orthonexus-sidebar-collapsed";

function loadSidebarCollapsed(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
}

interface AppShellContextValue {
  /** Sidebar globale (slide-over mobile) ouverte. */
  mobileNavOpen: boolean;
  /** Sidebar globale visuellement repliée (true = étroite, false = déployée). */
  sidebarCollapsed: boolean;
}

const AppShellContext = React.createContext<AppShellContextValue>({
  mobileNavOpen: false,
  sidebarCollapsed: false,
});

/** Expose l'état de la sidebar globale aux pages internes (ex. agenda) pour
 *  pouvoir fermer leurs propres panneaux quand la sidebar s'ouvre. */
export function useAppShell(): AppShellContextValue {
  return React.useContext(AppShellContext);
}

export function AppShell() {
  const narrow = useNarrowLayout();
  const [collapsed, setCollapsed] = React.useState<boolean>(() =>
    loadSidebarCollapsed(),
  );
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  React.useEffect(() => {
    registerAppBridge({
      navigate: (to) => {
        void router.navigate({ to });
      },
    });
  }, []);

  React.useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (narrow) setCollapsed(true);
  }, [narrow]);

  const toggleSidebar = () => {
    if (narrow) {
      setMobileNavOpen((o) => !o);
      return;
    }
    setCollapsed((c) => {
      const next = !c;
      if (typeof localStorage !== "undefined") {
        try {
          localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "1" : "0");
        } catch {
          /* non-fatal */
        }
      }
      return next;
    });
  };

  const sidebarCollapsed = collapsed && !mobileNavOpen;

  const bodyClass = [
    "app-body",
    sidebarCollapsed ? "collapsed" : "",
    mobileNavOpen ? "mobile-nav-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <ChatDockProvider>
      <DockingEditBarHostProvider>
        <AppShellContext.Provider value={{ mobileNavOpen, sidebarCollapsed }}>
          <div className="app">
            <PracticeRouteSync />
            <TopBar
              sidebarCollapsed={sidebarCollapsed}
              onToggleSidebar={toggleSidebar}
            />
            <div className={bodyClass}>
              <button
                type="button"
                className="app-sidebar-backdrop"
                aria-label="Fermer le menu"
                onClick={() => setMobileNavOpen(false)}
              />
              <div className="app-sidebar">
                <Sidebar collapsed={sidebarCollapsed} />
              </div>
              <main className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-sf-surface">
                <Outlet />
              </main>
            </div>
          </div>
        </AppShellContext.Provider>
      </DockingEditBarHostProvider>
    </ChatDockProvider>
  );
}
